// Expense Type Enum
export enum ExpenseType {
  Home = 0,
  Personal = 1
}

// Home Expenses API Types
export interface HomeExpenseDto {
  publicId: string;
  date: string;
  group: string;
  amount: number;
  account: string; // Keep as string for display (Account.Name from backend)
  observations?: string;
  isIncome: boolean;
  type: ExpenseType;
  homeExpenseGroup?: HomeExpenseGroupDto;
}

export interface HomeExpenseGroupDto {
  publicId: string;
  name: string;
  description?: string;
  isIncomeGroup: boolean;
  color?: string;
  isShoppingDefault?: boolean;
  category?: HomeExpenseGroupCategoryDto;
}

// Home Expense Group Category types
export interface HomeExpenseGroupCategoryDto {
  publicId: string;
  name: string;
  description?: string;
  color?: string;
  sortOrder: number;
}

export interface CreateHomeExpenseGroupCategoryCommand {
  name: string;
  description?: string;
  color?: string;
  sortOrder: number;
}

export interface UpdateHomeExpenseGroupCategoryCommand {
  name: string;
  description?: string;
  color?: string;
  sortOrder: number;
}

/** The fields of an expense the user fills in; its type comes from the page. */
export interface HomeExpenseFields {
  date: string; // ISO date string (YYYY-MM-DD)
  amount: number;
  observations?: string;
  homeExpenseGroupPublicId: string; // Guid as string
  accountPublicId: string; // Guid as string
}

export interface CreateHomeExpenseCommand extends HomeExpenseFields {
  type: ExpenseType; // Required: the API refuses an expense without it rather than sharing it with the household
}

export interface CreateMultipleHomeExpensesCommand {
  expenses: CreateHomeExpenseCommand[];
}

export interface HomeExpenseSummaryDto {
  group: string;
  totalAmount: number;
  count: number;
  isIncomeGroup: boolean;
}

// Account types
export interface AccountDto {
  publicId: string;
  name: string;
  description?: string;
  color?: string;
  isPrimary: boolean;
  type: ExpenseType;
}

export interface CreateAccountCommand {
  name: string;
  description?: string;
  color?: string;
  isPrimary: boolean;
  type?: ExpenseType; // Optional, defaults to Home on backend
}

export interface UpdateAccountCommand {
  publicId: string;
  name: string;
  description?: string;
  color?: string;
  isPrimary: boolean;
}

// Category usage analysis types
export interface CategoryUsageDto {
  categoryName: string;
  categoryColor: string;
  groupName: string;
  groupColor: string;
  totalAmount: number;
  groupCount: number;
  expenseCount: number;
  percentage: number;
  year: number;
  isIncomeCategory: boolean;
}

// Category vs Month breakdown analytics
interface CategoryMonthExpenseResumeDto {
  publicId: string;
  date: string;
  groupName: string;
  accountName: string;
  observations?: string;
  amount: number;
  isIncome: boolean;
}

export interface CategoryMonthStatDto {
  categoryName: string;
  categoryColor: string;
  isIncomeCategory: boolean;
  year: number;
  month: number; // 1..12
  totalAmount: number; // absolute value
  averageAmount: number; // absolute value across months
  budgetAmount: number;
  previousYearAverageAmount: number;
  overAverage: boolean;
  overstepExpenses: CategoryMonthExpenseResumeDto[];
}

// Category budgets
export interface CategoryYearBudgetDto {
  categoryPublicId: string;
  categoryName: string;
  year: number;
  monthlyBudgetAmount: number;
  previousYearAverageAmount: number;
  categoryColor: string;
}

interface UpsertCategoryYearBudgetItem {
  categoryPublicId: string;
  monthlyBudgetAmount: number;
}

export interface UpsertCategoryYearBudgetsCommand {
  year: number;
  items: UpsertCategoryYearBudgetItem[];
}
